import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import actions from 'actions';
import {
  buildCreateSpotPayload,
  buildSpatialSpotIndex,
  getLocationProvenance,
  getPhotoPosition,
  getPhotoUrl,
  normalizeDetectivePhoto,
  normalizeUnsplashPhoto,
  normalizeUserPhoto,
} from './photoModel';
import SuggestionPanel, { getSuggestionTone, getSuggestionToneStyle } from './SuggestionPanel';
import LocationMap from './LocationMap';
import { generateSpotDescription } from './description';
import {
  buildCompactApplyResult,
  getApprovalBlockReason,
  getCandidateSpotIds,
  getDetectiveProvenance,
  getGpsTrust,
  getPhotoGroups,
  getPreloadPlan,
  getQueueBadge,
  getQueueWithDeferredLast,
  getSlugCollisionSpot,
  getSpotsAround,
  getSubscriptionLevels,
  isAbstention,
  isMissingImageError,
  sortQueueBySubscription,
  withRepresentativeFirst,
  addUsage,
  estimateRequestTokens,
  getAffordableModel,
  getModelUsage,
  hasComplimentaryTokensLeft,
  hasRoomOn,
  isFallbackModel,
  pickModel,
  readStoredUsage,
  withServerUsage,
  writeStoredUsage,
  MODEL_ROSTER,
  QUEUE,
  isUnsplashQueue,
  getSuggestionValidationError,
  isTerminalSuggestionError,
  shouldIncludeInAutoModerationSession,
  splitGroupedPhotos,
} from './model';
import './styles.scss';

const QUEUE_LIMIT = 10000;
// How far into the Unsplash pile is finished. The pile is a quarter of a million
// photos, so a session that always started at the beginning would judge the same
// few every day.
//
// The key carries a version because the value under the old one means something
// else: it was the end of the block the last page was read from, so it stands far
// ahead of photos nobody ever decided. Reading it now would skip every one of
// them, and there is nothing to migrate - the honest answer is to start again
// from the top and let the photos already decided fall out of the query on their
// own, which is exactly what they do.
const UNSPLASH_CURSOR_KEY = 'autoModerationUnsplashCursorV2';
// The near-a-spot pass walks the same pile looking for something else, so it
// cannot share a cursor with the full one. "Far from every spot" is also not
// permanent - the owner creates spots all day - which is the other reason these
// two must never be merged.
const UNSPLASH_NEAR_CURSOR_KEY = 'autoModerationUnsplashNearCursorV1';
// The safe judge walks the pile with a filter of its own, so it keeps its own
// place in it too.
const UNSPLASH_SAFE_CURSOR_KEY = 'autoModerationUnsplashSafeCursorV1';
// Its phone-coordinate pass is a walk of its own: one pending photo in 140
// carries one, so sharing a cursor with the tags would leave them all behind.
const UNSPLASH_SAFE_CAMERA_CURSOR_KEY = 'autoModerationUnsplashSafeCameraCursorV1';

// One page of the Unsplash pile, from wherever that pass stopped last time. An
// empty nextAfter is an answer, not a missing value: the backend reached the end
// of the collection, so the next session starts from the top instead of standing
// at the end returning nothing.
const readUnsplashPage = async ({ nearSpots = false, safe = false, pointKind = '' }) => {
  const cursorKey = safe
    ? pointKind === 'camera'
      ? UNSPLASH_SAFE_CAMERA_CURSOR_KEY
      : UNSPLASH_SAFE_CURSOR_KEY
    : nearSpots
      ? UNSPLASH_NEAR_CURSOR_KEY
      : UNSPLASH_CURSOR_KEY;
  const stored = window.localStorage.getItem(cursorKey) || '';
  // Every camera coordinate behind the regular queue's cursor was decided there
  // already - the first phone photo still pending stands 59,000 documents into
  // the pile - so the phone pass starts from whichever of the two is further on
  // instead of reading thirty thousand finished documents per session to get
  // there. Document ids are plain ASCII, so comparing them as strings is the
  // order the pile is walked in.
  const regular = window.localStorage.getItem(UNSPLASH_CURSOR_KEY) || '';
  const page = await actions.autoModeration.getUnsplashQueue({
    after: safe && pointKind === 'camera' && regular > stored ? regular : stored,
    nearSpots,
    safe,
    pointKind,
  });

  if (page.nextAfter) {
    window.localStorage.setItem(cursorKey, page.nextAfter);
  } else {
    window.localStorage.removeItem(cursorKey);
  }

  return { ...page, photos: Array.isArray(page.photos) ? page.photos : [] };
};

// The near-a-spot pass is about a fortieth of the collection and the front of
// the pile is thinner than that, so it regularly runs out of scan budget before
// it fills a page - four photos out of thirty thousand documents read. Those
// four are still the best four, so they are kept and the session is topped up
// from the full pile behind them rather than ending after four.
const UNSPLASH_SESSION_MIN = 20;

const mergeUnsplashPages = (near, rest) => {
  const alreadyServed = new Set(near.photos.map(photo => photo.id));

  return {
    ...rest,
    // A mixed session is not a near-spot session, and the badge says so.
    nearSpots: false,
    photos: [...near.photos, ...rest.photos.filter(photo => !alreadyServed.has(photo.id))],
  };
};
const MAX_FEEDBACK_LENGTH = 2000;
const INITIAL_SESSION_STATS = Object.freeze({
  accepted: 0,
  challenged: 0,
  deferred: 0,
  skipped: 0,
  // Photos the safe judge was not sure of. They never reach the screen: they
  // leave the session and wait, untouched, for the regular Unsplash queue.
  screened: 0,
});
const INITIAL_CREATE_OPTIONS = Object.freeze({
  skipDescription: false,
  superSpot: false,
  superReason: '',
});
// The two notes the owner writes most when he turns a suggestion down. One
// click sends the note as if he had typed it.
const QUICK_FEEDBACK = Object.freeze(['This is not a spot', 'Not interesting enough']);
// And the one he writes when the photo was sent to a spot it does not belong to.
const QUICK_ASSIGN_FEEDBACK = 'This is not the same spot';
// Past Super Spots are asked about at the start of the session, all in a row,
// and never so many that the session turns into a questionnaire.
const SUPER_SPOT_QUESTIONS_PER_SESSION = 10;
// The bench runs on the fallback model's complimentary tokens only, a few photos
// at a time, each reserved at the same estimate the live queue uses.
const BENCH_BATCH_SIZE = 20;
const BENCH_CONCURRENCY = 4;
const INITIAL_BENCH_STATS = Object.freeze({
  done: 0,
  skipped: 0,
  failed: 0,
  remaining: null,
  stoppedFor: '',
});

const runWithConcurrency = async (items, concurrency, worker) => {
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const item = items[next];

      next += 1;
      await worker(item);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, lane));
};

const getActionPastTense = action => {
  if (action === 'assign_spot') {
    return 'assigned';
  }

  if (action === 'create_spot') {
    return 'created';
  }

  if (action === 'reject') {
    return 'rejected';
  }

  // Nothing was moderated: the photo left the Detective list and keeps being
  // shown in the app until somebody recognises it.
  if (action === 'needs_more_info') {
    return 'waiting for a detective';
  }

  // Neither applies anything, and the background task is only recording the
  // owner's note, so the label says which kind of hand-off it was.
  if (action === 'needs_gps_fix') {
    return 'gps by hand';
  }

  return action === 'needs_review' ? 'note' : 'applied';
};

const getErrorMessage = error =>
  (error && error.message) || 'Something went wrong. The current photo has not advanced.';

const QueueLoading = () => (
  <main className="AutoModeration">
    <div className="auto-page-state --loading">
      <div>
        <span className="auto-kicker">Auto Mod · Training mode</span>
        <h1>Loading the pending queue</h1>
        <p>Gathering user photos before asking the model for one decision.</p>
        <div className="auto-loading-track"><span /></div>
      </div>
    </div>
  </main>
);

const SuggestionLoading = () => (
  <div className="auto-suggestion-loading" role="status">
    <span className="auto-kicker">Model working</span>
    <h1>Preparing one proposal</h1>
    <p>The image, location, nearby spots, and moderation evidence are being checked together.</p>
    <div className="auto-scan-lines">
      <span />
      <span />
      <span />
    </div>
  </div>
);

const formatTokens = value =>
  value >= 1000000
    ? `${(value / 1000000).toFixed(2)}M`
    : value >= 1000
      ? `${Math.round(value / 1000)}k`
      : String(value);

// One thin line per model family, filling as its complimentary daily tokens are
// spent. The active one is the model the next suggestion will use, so the moment
// the top bar fills the line below it starts moving instead.
const TokenBudgetBar = ({ usage, activeModel }) => (
  <div className="auto-token-budget" aria-label="Complimentary daily tokens">
    {MODEL_ROSTER.map(model => {
      const spent = getModelUsage(usage, model.id);
      const ratio = Math.min(spent / model.dailyTokenLimit, 1);

      return (
        <div
          className={`auto-token-bar ${model.id === activeModel ? '--active' : ''}`}
          key={model.id}
          title={`${model.label}: ${formatTokens(spent)} of ${formatTokens(
            model.dailyTokenLimit
          )} complimentary tokens today`}
        >
          <span style={{ width: `${ratio * 100}%`, background: model.colour }} />
        </div>
      );
    })}
  </div>
);

const AutoModeration = () => {
  const [photos, setPhotos] = useState([]);
  // Which of the two queues the page is working through. User photos first,
  // because somebody uploaded them and is waiting; Detective afterwards, on
  // whatever is left of the day's complimentary tokens.
  const [queue, setQueue] = useState(QUEUE.USER_PHOTOS);
  const [isLoadingDetective, setIsLoadingDetective] = useState(false);
  const [detectiveCount, setDetectiveCount] = useState(null);
  const [isLoadingUnsplash, setIsLoadingUnsplash] = useState(false);
  const [isLoadingSafe, setIsLoadingSafe] = useState(false);
  const [unsplashCount, setUnsplashCount] = useState(null);
  const [queueMetadata, setQueueMetadata] = useState(null);
  // Where the owner dropped the pin himself, overriding whatever the model
  // proposed. Only a create_spot has a point to override.
  const [correctedPoint, setCorrectedPoint] = useState(null);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionStats, setSessionStats] = useState(() => ({ ...INITIAL_SESSION_STATS }));
  const [isQueueLoading, setIsQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionStatus, setSuggestionStatus] = useState('idle');
  const [suggestionError, setSuggestionError] = useState(null);
  const [suggestionRetry, setSuggestionRetry] = useState(0);
  const [hydratedTargetSpot, setHydratedTargetSpot] = useState(null);
  const [isHydratingTarget, setIsHydratingTarget] = useState(false);
  const [targetHydrationFailed, setTargetHydrationFailed] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [note, setNote] = useState('');
  const [actionError, setActionError] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [photoDimensions, setPhotoDimensions] = useState(null);
  const [groupedCount, setGroupedCount] = useState(0);
  const [backgroundTasks, setBackgroundTasks] = useState([]);
  const [stageView, setStageView] = useState('photo');
  const [createOptions, setCreateOptions] = useState(() => ({ ...INITIAL_CREATE_OPTIONS }));
  const [mapOpenedFor, setMapOpenedFor] = useState(null);
  // Which photos further down the queue already have their proposal in hand.
  const [readySuggestionIds, setReadySuggestionIds] = useState(() => new Set());
  const [tokenUsage, setTokenUsage] = useState(() => readStoredUsage(window.localStorage));
  // Bumped whenever a suggestion request settles, so the next photo waiting to
  // be preloaded is asked for even when the answer spent nothing.
  const [settledRequests, setSettledRequests] = useState(0);
  // Past Super Spots waiting to be asked about, and the one on screen.
  const [superQuestions, setSuperQuestions] = useState([]);
  const [superQuestion, setSuperQuestion] = useState(null);
  const [superReason, setSuperReason] = useState('');
  const [superAsked, setSuperAsked] = useState(0);
  const [benchRunning, setBenchRunning] = useState(false);
  const [benchStats, setBenchStats] = useState(() => ({ ...INITIAL_BENCH_STATS }));

  const mountedRef = useRef(false);
  const spotsRef = useRef([]);
  const spotIndexRef = useRef(null);
  const slugsRef = useRef(new Set());
  const queueGenerationRef = useRef(0);
  const suggestionGenerationRef = useRef(0);
  const hydrationGenerationRef = useRef(0);
  const feedbackRef = useRef(null);
  const preloadedUrlsRef = useRef(new Set());
  const subscriptionLevelsRef = useRef(new Map());
  const tokenUsageRef = useRef(tokenUsage);
  const inspectorRef = useRef(null);
  const groupedPhotosRef = useRef([]);
  const detectiveLoadedRef = useRef(false);
  const unsplashLoadedRef = useRef(false);
  const safeLoadedRef = useRef(false);
  const suggestionRequestsRef = useRef(new Map());
  const pendingUsageRef = useRef(new Set());
  const createPayloadsRef = useRef(new Map());
  const regeneratedForSlugRef = useRef(new Set());
  const skipRef = useRef(() => {});
  const superRunRef = useRef(false);
  const superOpenRef = useRef(false);
  const benchRunningRef = useRef(false);
  const benchFailedRef = useRef(new Set());
  const decidedCount =
    sessionStats.accepted + sessionStats.challenged + sessionStats.deferred + sessionStats.skipped;

  const currentPhoto = photos[0] || null;
  const currentPhotoId = currentPhoto ? currentPhoto.id : null;
  // Read from the photo in hand, not from the phase: the queues never interleave,
  // but the phase changes a beat before the queue does.
  const currentQueue = currentPhoto ? currentPhoto.queue || QUEUE.USER_PHOTOS : queue;
  const isDetective = currentQueue === QUEUE.DETECTIVE;
  const photoUrl = currentPhoto ? getPhotoUrl(currentPhoto) : null;
  const photoPosition = currentPhoto ? getPhotoPosition(currentPhoto) : null;
  // A Detective photo has no camera metadata to weigh: the point came from the
  // people who recognised it, so the trust line reports them instead.
  const provenance = !currentPhoto
    ? null
    : isDetective
      ? getDetectiveProvenance(currentPhoto)
      : getLocationProvenance(currentPhoto);
  const currentPhotoDimensions =
    photoDimensions && photoDimensions.photoId === currentPhotoId ? photoDimensions : null;
  const suggestionValidationError = useMemo(
    () => (suggestion && currentPhoto ? getSuggestionValidationError(suggestion, currentPhoto) : null),
    [currentPhoto, suggestion]
  );
  const suggestionApprovalBlockReason = useMemo(
    () => (suggestion && currentPhoto ? getApprovalBlockReason(suggestion, currentPhoto) : null),
    [currentPhoto, suggestion]
  );
  // A spot now holds the slug this proposal wants, so the proposal was built
  // before that spot existed and is answering a question that has changed. The
  // photo belongs to that spot, but the frontend must not turn a create into an
  // assign by itself: the backend refuses an applied mutation that does not
  // match the proposal, and rightly so. The model is asked again instead, with
  // the spot now among its candidates.
  const slugCollisionSpot = useMemo(
    () => getSlugCollisionSpot(suggestion, spotsRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestion, currentPhotoId]
  );
  const previewApprovalBlockReason =
    !photoUrl || imageFailed
      ? 'The photo preview is unavailable. Review it in Mod before accepting this suggestion.'
      : !currentPhotoDimensions
        ? 'Wait for the photo preview to finish loading before accepting this suggestion.'
        : null;
  const approvalBlockReason =
    suggestionApprovalBlockReason ||
    (slugCollisionSpot
      ? `“${suggestion.newSpot.slug}” already belongs to a spot created after this proposal was built. Asking the model again with that spot as a candidate.`
      : null) ||
    previewApprovalBlockReason;
  const terminalSuggestionError = isTerminalSuggestionError(suggestionError);
  const suggestionCandidates =
    suggestion && Array.isArray(suggestion.candidates) ? suggestion.candidates : [];
  const gpsTrust = useMemo(() => getGpsTrust(provenance), [provenance]);
  // Read ahead is only worth anything while the answers arrive before the photos
  // do, and that is invisible until it stops happening. The current photo is not
  // counted: it is being decided, not waited on.
  const readyAheadCount = useMemo(
    () => photos.slice(1).filter(photo => readySuggestionIds.has(photo.id)).length,
    [photos, readySuggestionIds]
  );
  // The map is only built once the owner asks for it, and then survives every
  // switch back to the photo until the queue moves on.
  const hasOpenedMap = Boolean(currentPhotoId) && mapOpenedFor === currentPhotoId;
  // Read from the session's own spot list, so the map is not limited to the
  // handful of candidates the model was shown.
  const mapSpots = useMemo(() => {
    if (!hasOpenedMap) {
      return [];
    }

    // The light spot list carries slugs, not names; the candidates the backend
    // read do carry names, so the ones that matter get a readable label.
    const names = new Map(
      suggestionCandidates.filter(candidate => candidate.name).map(c => [c.id, c.name])
    );

    return getSpotsAround(spotsRef.current, currentPhoto).map(spot =>
      names.has(spot.id) ? { ...spot, name: names.get(spot.id) } : spot
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhotoId, hasOpenedMap, suggestion]);

  // The spot list already carries every slug, so the whole collection is read
  // once per session and each spot created here is added to it in place.
  const setSpotUniverse = useCallback(spots => {
    spotsRef.current = spots;
    spotIndexRef.current = buildSpatialSpotIndex(spots);
    slugsRef.current = new Set(
      spots
        .map(spot => String((spot && spot.slug) || '').trim().toLocaleLowerCase())
        .filter(Boolean)
    );
  }, []);

  const loadQueue = useCallback(async () => {
    const generation = queueGenerationRef.current + 1;
    queueGenerationRef.current = generation;
    setIsQueueLoading(true);
    setQueueError(null);
    setActionError(null);

    try {
      await actions.autoModeration.recoverApplying().catch(() => null);

      if (!mountedRef.current || generation !== queueGenerationRef.current) {
        return;
      }

      // The spot universe is read once here, off the CDN rather than out of
      // Firestore, plus whatever was created since that file was built. Each
      // suggestion then only sends the nearby spot ids, so no request re-reads
      // the whole collection. The subscriber list is read once too, and only
      // carries ids and levels.
      const [{ photos: pendingPhotos, queue }, lightSpots, subscribers, serverUsage] =
        await Promise.all([
          actions.userPhotos.getHydratedsWithLocation({ limit: QUEUE_LIMIT }),
          actions.spots.getUniverse(),
          actions.user.getSubscribers().catch(() => []),
          actions.autoModeration.getTokenUsage().catch(() => null),
        ]);

      if (!mountedRef.current || generation !== queueGenerationRef.current) {
        return;
      }

      // Before the first model is picked: the nightly run may have spent part of
      // today's allowance with this page closed.
      const seededUsage = withServerUsage(tokenUsageRef.current, serverUsage);

      tokenUsageRef.current = seededUsage;
      writeStoredUsage(window.localStorage, seededUsage);
      setTokenUsage(seededUsage);

      const levels = getSubscriptionLevels(subscribers);

      setSpotUniverse(lightSpots);

      const { single: sessionPhotos, grouped } = splitGroupedPhotos(
        pendingPhotos.filter(shouldIncludeInAutoModerationSession).map(normalizeUserPhoto),
        spotIndexRef.current
      );

      subscriptionLevelsRef.current = levels;
      // Reloading always starts a fresh session at the user photos, so Detective
      // is offered again once they run out.
      detectiveLoadedRef.current = false;
      unsplashLoadedRef.current = false;
      setQueue(QUEUE.USER_PHOTOS);
      setDetectiveCount(null);
      setGroupedCount(grouped.length);
      setPhotos(sortQueueBySubscription(sessionPhotos, levels));
      groupedPhotosRef.current = grouped;
      setQueueMetadata(queue || null);
      setSessionTotal(sessionPhotos.length);
      setSessionStats({ ...INITIAL_SESSION_STATS });
      setSuggestion(null);
      setSuggestionStatus('idle');
      setSuggestionError(null);
      setNote('');
      setCreateOptions({ ...INITIAL_CREATE_OPTIONS });
      // Background tasks are deliberately kept: they belong to decisions that
      // were already applied, not to the session. Clearing them here threw away
      // the description still being written for the spot just created, and took
      // the retry button for anything that failed with it.
      createPayloadsRef.current.clear();
      regeneratedForSlugRef.current.clear();
      suggestionRequestsRef.current.clear();
      setReadySuggestionIds(new Set());
    } catch (error) {
      if (mountedRef.current && generation === queueGenerationRef.current) {
        setQueueError(error);
      }
    } finally {
      if (mountedRef.current && generation === queueGenerationRef.current) {
        setIsQueueLoading(false);
      }
    }
  }, [setSpotUniverse]);

  // The second half of a session. Unsplash photos that NoFilter users located
  // from the app: already approved in Curation, so the only questions left are
  // which spot they belong to and whether anybody yet knows where they are.
  // They join the same queue and keep the same spot universe.
  const loadDetectiveQueue = useCallback(async () => {
    if (detectiveLoadedRef.current) {
      return;
    }

    detectiveLoadedRef.current = true;
    setIsLoadingDetective(true);

    try {
      const { photos: detectivePhotos } = await actions.autoModeration.getDetectiveQueue();

      if (!mountedRef.current) {
        return;
      }

      const sessionPhotos = detectivePhotos
        .filter(shouldIncludeInAutoModerationSession)
        .map(normalizeDetectivePhoto);

      setDetectiveCount(sessionPhotos.length);
      setQueue(QUEUE.DETECTIVE);
      // The pending count in the topbar belongs to the user photo queue.
      setQueueMetadata(null);
      setPhotos(sessionPhotos);
      setSessionTotal(current => current + sessionPhotos.length);
      setSuggestion(null);
      setSuggestionStatus('idle');
      setSuggestionError(null);
      setActionError(null);
    } catch (error) {
      if (mountedRef.current) {
        setDetectiveCount(0);
        setQueueError(error);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingDetective(false);
      }
    }
  }, []);

  // The third phase: the safe Unsplash judge. Most of its photos never reach the
  // screen - it drops everything it is not sure of - so a page of them is cheap,
  // and what does reach the owner is the answer he is being asked to grade.
  const loadUnsplashSafeQueue = useCallback(async () => {
    if (safeLoadedRef.current) {
      return;
    }

    safeLoadedRef.current = true;
    setIsLoadingSafe(true);

    try {
      // The phone's own coordinate first: it is where the photographer stood nine
      // times in ten, a tag one time in two. Tags only top the session up once
      // that pass runs thin.
      const camera = await readUnsplashPage({ safe: true, pointKind: 'camera' });
      const page =
        camera.photos.length >= UNSPLASH_SESSION_MIN
          ? camera
          : mergeUnsplashPages(camera, await readUnsplashPage({ safe: true, pointKind: 'tag' }));

      if (!mountedRef.current) {
        return;
      }

      const sessionPhotos = page.photos
        .filter(shouldIncludeInAutoModerationSession)
        .map(photo => ({ ...normalizeUnsplashPhoto(photo), queue: QUEUE.UNSPLASH_SAFE }));

      setQueue(QUEUE.UNSPLASH_SAFE);
      setQueueMetadata(null);
      setPhotos(sessionPhotos);
      setSessionTotal(current => current + sessionPhotos.length);
      setSuggestion(null);
      setSuggestionStatus('idle');
      setSuggestionError(null);
      setActionError(null);
    } catch (error) {
      if (mountedRef.current) {
        setQueueError(error);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingSafe(false);
      }
    }
  }, []);

  // The fourth and last phase. Unsplash photos nobody has located, standing on the
  // coordinate the camera wrote. The pile is far larger than a session, so it is
  // read one page at a time and the cursor carries on where the last page ended.
  const loadUnsplashQueue = useCallback(async () => {
    if (unsplashLoadedRef.current) {
      return;
    }

    unsplashLoadedRef.current = true;
    setIsLoadingUnsplash(true);

    try {
      // The photos close to a spot that already exists come first, because they
      // are the ones that end in a decision: inside 500 m the model assigns or
      // rejects two times out of three, and out in the open it mostly answers
      // that nobody can place the photo - which is true, and costs a model call
      // to find out. The two passes walk the pile separately and keep their own
      // cursors, so a photo the near pass steps over still gets its turn.
      const near = await readUnsplashPage({ nearSpots: true });
      const page =
        near.photos.length >= UNSPLASH_SESSION_MIN
          ? near
          : mergeUnsplashPages(near, await readUnsplashPage({ nearSpots: false }));

      if (!mountedRef.current) {
        return;
      }

      const sessionPhotos = page.photos
        .filter(shouldIncludeInAutoModerationSession)
        .map(normalizeUnsplashPhoto);

      setUnsplashCount(sessionPhotos.length);
      setQueue(QUEUE.UNSPLASH);
      setQueueMetadata({ nearSpots: page.nearSpots === true });
      setPhotos(sessionPhotos);
      setSessionTotal(current => current + sessionPhotos.length);
      setSuggestion(null);
      setSuggestionStatus('idle');
      setSuggestionError(null);
      setActionError(null);
    } catch (error) {
      if (mountedRef.current) {
        setUnsplashCount(0);
        setQueueError(error);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingUnsplash(false);
      }
    }
  }, []);

  // After Detective empties, on the same complimentary-token condition.
  useEffect(() => {
    if (
      isQueueLoading ||
      isLoadingDetective ||
      isLoadingSafe ||
      queue !== QUEUE.DETECTIVE ||
      photos.length > 0 ||
      safeLoadedRef.current ||
      !hasComplimentaryTokensLeft(tokenUsage)
    ) {
      return;
    }

    loadUnsplashSafeQueue();
  }, [
    isLoadingDetective,
    isLoadingSafe,
    isQueueLoading,
    loadUnsplashSafeQueue,
    photos.length,
    queue,
    tokenUsage,
  ]);

  // After the safe judge's page empties, on the same condition.
  useEffect(() => {
    if (
      isQueueLoading ||
      isLoadingSafe ||
      isLoadingUnsplash ||
      queue !== QUEUE.UNSPLASH_SAFE ||
      photos.length > 0 ||
      unsplashLoadedRef.current ||
      !hasComplimentaryTokensLeft(tokenUsage)
    ) {
      return;
    }

    loadUnsplashQueue();
  }, [
    isLoadingSafe,
    isLoadingUnsplash,
    isQueueLoading,
    loadUnsplashQueue,
    photos.length,
    queue,
    tokenUsage,
  ]);

  // The moment the user photos run out. Only while the complimentary allowance
  // still has room: past it every suggestion is billed, and Detective is a
  // backlog that can always wait for tomorrow's tokens.
  useEffect(() => {
    if (
      isQueueLoading ||
      isLoadingDetective ||
      queue !== QUEUE.USER_PHOTOS ||
      photos.length > 0 ||
      // Grouped photos are still being ordered and have not joined the queue
      // yet. Opening Detective now would put the lost photos in front of user
      // photos somebody is waiting on.
      groupedCount > 0 ||
      groupedPhotosRef.current.length > 0 ||
      detectiveLoadedRef.current ||
      !hasComplimentaryTokensLeft(tokenUsage)
    ) {
      return;
    }

    loadDetectiveQueue();
  }, [
    groupedCount,
    isLoadingDetective,
    isQueueLoading,
    loadDetectiveQueue,
    photos.length,
    queue,
    tokenUsage,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    loadQueue();

    return () => {
      mountedRef.current = false;
      queueGenerationRef.current += 1;
      suggestionGenerationRef.current += 1;
      hydrationGenerationRef.current += 1;
    };
  }, [loadQueue]);

  // Suggestions can land together, so the running total lives in the ref and
  // the state only mirrors it for the bars. Reading localStorage inside each
  // response would let two answers landing together overwrite each other.
  const countTokenUsage = useCallback(usage => {
    const next = addUsage(tokenUsageRef.current, usage);

    tokenUsageRef.current = next;
    writeStoredUsage(window.localStorage, next);
    setTokenUsage(next);
  }, []);

  // What today has spent plus what the requests still in flight are expected to
  // spend. A model is chosen against this, never against the spent total alone:
  // requests sent together would otherwise all see the same room left in the
  // strong model's allowance and overrun it between them.
  const getCommittedUsage = useCallback(
    () =>
      [...pendingUsageRef.current].reduce(
        (usage, pending) => addUsage(usage, pending),
        tokenUsageRef.current
      ),
    []
  );

  // Replays the owner's past /moderation decisions through the Unsplash judge,
  // on the fallback model's complimentary tokens and nothing else. It stops by
  // itself the moment that allowance has no room for another bench photo, and
  // every token it spends is counted in the same bars as the live queue's.
  const startBench = useCallback(async () => {
    const spotIndex = spotIndexRef.current;

    if (benchRunningRef.current || !spotIndex) {
      return;
    }

    const benchModel = MODEL_ROSTER[MODEL_ROSTER.length - 1].id;
    // Why it stopped, written from inside the workers as well as the loop.
    const stop = { reason: 'stopped' };

    benchRunningRef.current = true;
    setBenchRunning(true);
    setBenchStats(current => ({ ...current, stoppedFor: '' }));

    try {
      while (benchRunningRef.current) {
        const batch = await actions.autoModeration.getBenchBatch({ limit: BENCH_BATCH_SIZE });
        const benchPhotos = (Array.isArray(batch && batch.photos) ? batch.photos : []).filter(
          photo => !benchFailedRef.current.has(photo.photoId)
        );

        if (mountedRef.current) {
          setBenchStats(current => ({ ...current, remaining: batch ? batch.remaining : null }));
        }

        if (benchPhotos.length === 0) {
          stop.reason = batch && batch.remaining > 0 ? 'only failing photos left' : 'bench finished';
          break;
        }

        await runWithConcurrency(benchPhotos, BENCH_CONCURRENCY, async photo => {
          if (!benchRunningRef.current) {
            return;
          }

          const hidden = new Set(photo.hiddenSpotIds || []);
          const candidateSpotIds = getCandidateSpotIds(spotIndex, photo).filter(
            id => !hidden.has(id)
          );
          const cost = estimateRequestTokens(candidateSpotIds.length);

          if (!hasRoomOn(getCommittedUsage(), benchModel, cost)) {
            benchRunningRef.current = false;
            stop.reason = 'no free tokens left today';
            return;
          }

          const pending = { model: benchModel, totalTokens: cost };

          pendingUsageRef.current.add(pending);

          try {
            const result = await actions.autoModeration.runBenchPhoto({
              photoId: photo.photoId,
              candidateSpotIds,
              model: benchModel,
            });

            if (result && result.usage) {
              countTokenUsage(result.usage);
            }

            if (mountedRef.current) {
              setBenchStats(current => ({
                ...current,
                done: current.done + 1,
                skipped: current.skipped + (result && result.skipped ? 1 : 0),
              }));
            }
          } catch (error) {
            // Google's free calls for the month are spoken for: nothing else
            // on the bench can run until next month either.
            if (error && error.code === 'AUTO_MODERATION_BENCH_PLACES_BUDGET') {
              benchRunningRef.current = false;
              stop.reason = 'free Google calls left this month are kept for live moderation';
              return;
            }

            benchFailedRef.current.add(photo.photoId);

            if (mountedRef.current) {
              setBenchStats(current => ({ ...current, failed: current.failed + 1 }));
            }
          } finally {
            pendingUsageRef.current.delete(pending);

            if (mountedRef.current) {
              setSettledRequests(count => count + 1);
            }
          }
        });
      }
    } catch (error) {
      stop.reason = getErrorMessage(error);
    } finally {
      benchRunningRef.current = false;

      if (mountedRef.current) {
        setBenchRunning(false);
        setBenchStats(current => ({ ...current, stoppedFor: stop.reason }));
      }
    }
  }, [countTokenUsage, getCommittedUsage]);

  const stopBench = useCallback(() => {
    benchRunningRef.current = false;
  }, []);

  useEffect(
    () => () => {
      benchRunningRef.current = false;
    },
    []
  );

  // One suggestion per photo, kept so the photo waiting behind the current one
  // can be asked for in advance and be ready the moment it comes up.
  const requestSuggestion = useCallback((photo, { regenerate = false } = {}) => {
    if (!photo || !photo.id) {
      return Promise.reject(new Error('No photo to ask about.'));
    }

    const cached = suggestionRequestsRef.current.get(photo.id);

    if (cached && !regenerate) {
      return cached;
    }

    const spotIndex = spotIndexRef.current;
    const candidateSpotIds = spotIndex ? getCandidateSpotIds(spotIndex, photo) : [];
    // Chosen per request from what this photo will actually cost: every
    // candidate image adds tokens, so a photo with nothing nearby still fits in
    // an allowance an expensive one no longer does.
    const model = pickModel(getCommittedUsage(), candidateSpotIds.length);
    // An object of its own, so a regenerated request for the same photo never
    // releases what an earlier one still holds.
    const pending = { model, totalTokens: estimateRequestTokens(candidateSpotIds.length) };
    const request = spotIndex
      ? actions.autoModeration.getSuggestion({
          photoId: photo.id,
          // Which collection the photo lives in and which rules it is judged by.
          queue: photo.queue,
          candidateSpotIds,
          model,
          // A Detective photo somebody located again after it was parked comes
          // back with a stored decision that answered a different question, and
          // replaying it would put the old answer back on the screen.
          regenerate: regenerate || photo.needsFreshSuggestion === true,
        })
      : Promise.reject(new Error('The spot list is not loaded. Reload the pending queue.'));

    pendingUsageRef.current.add(pending);

    // A failed request must not be cached: retrying has to hit the backend again.
    const tracked = request
      .then(suggestion => {
        pendingUsageRef.current.delete(pending);
        setSettledRequests(count => count + 1);

        // Only a suggestion that was generated now carries usage: replayed ones
        // report null, so nothing is counted twice.
        if (suggestion && suggestion.usage) {
          countTokenUsage(suggestion.usage);
        }

        // The safe judge was not sure. The photo leaves the session without ever
        // being shown, and nothing is recorded against it here.
        if (suggestion && suggestion.unsure) {
          setPhotos(current => current.filter(item => item.id !== photo.id));
          setSessionStats(current => ({ ...current, screened: (current.screened || 0) + 1 }));
          return suggestion;
        }

        setReadySuggestionIds(current => {
          if (current.has(photo.id)) {
            return current;
          }

          const next = new Set(current);
          next.add(photo.id);
          return next;
        });

        return suggestion;
      })
      .catch(error => {
        pendingUsageRef.current.delete(pending);
        setSettledRequests(count => count + 1);
        suggestionRequestsRef.current.delete(photo.id);
        setReadySuggestionIds(current => {
          if (!current.has(photo.id)) {
            return current;
          }

          const next = new Set(current);
          next.delete(photo.id);
          return next;
        });
        throw error;
      });

    suggestionRequestsRef.current.set(photo.id, tracked);
    return tracked;
  }, [countTokenUsage, getCommittedUsage]);

  useEffect(() => {
    if (!currentPhotoId || isQueueLoading) {
      return undefined;
    }

    const generation = suggestionGenerationRef.current + 1;
    suggestionGenerationRef.current = generation;
    setSuggestion(null);
    setSuggestionStatus('loading');
    setSuggestionError(null);
    setActionError(null);
    setFeedbackOpen(false);
    setFeedback('');
    setNote('');
    setCreateOptions({ ...INITIAL_CREATE_OPTIONS });

    requestSuggestion(currentPhoto)
      .then(nextSuggestion => {
        if (
          !mountedRef.current ||
          generation !== suggestionGenerationRef.current ||
          (nextSuggestion && nextSuggestion.unsure)
        ) {
          return;
        }

        const validationError = getSuggestionValidationError(nextSuggestion, currentPhoto);

        if (validationError) {
          throw new Error(validationError);
        }

        setSuggestion(nextSuggestion);
        setSuggestionStatus('ready');
      })
      .catch(error => {
        if (mountedRef.current && generation === suggestionGenerationRef.current) {
          setSuggestionError(error);
          setSuggestionStatus('error');
        }
      });

    return () => {
      if (generation === suggestionGenerationRef.current) {
        suggestionGenerationRef.current += 1;
      }
    };
  }, [currentPhoto, currentPhotoId, isQueueLoading, suggestionRetry]);

  // Once per photo only. If the model still proposes a taken slug on the second
  // pass it is not going to change its mind, and every attempt spends tokens
  // from the daily allowance, so the approval stays blocked and the owner
  // decides by hand.
  useEffect(() => {
    if (!slugCollisionSpot || !currentPhoto || regeneratedForSlugRef.current.has(currentPhotoId)) {
      return;
    }

    regeneratedForSlugRef.current.add(currentPhotoId);

    const generation = suggestionGenerationRef.current;

    setSuggestionStatus('loading');
    setSuggestion(null);

    requestSuggestion(currentPhoto, { regenerate: true })
      .then(nextSuggestion => {
        if (
          !mountedRef.current ||
          generation !== suggestionGenerationRef.current ||
          (nextSuggestion && nextSuggestion.unsure)
        ) {
          return;
        }

        const validationError = getSuggestionValidationError(nextSuggestion, currentPhoto);

        if (validationError) {
          throw new Error(validationError);
        }

        setSuggestion(nextSuggestion);
        setSuggestionStatus('ready');
      })
      .catch(error => {
        if (mountedRef.current && generation === suggestionGenerationRef.current) {
          setSuggestionError(error);
          setSuggestionStatus('error');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugCollisionSpot, currentPhotoId]);

  useEffect(() => {
    const targetSpot = suggestion && suggestion.action === 'assign_spot' && suggestion.targetSpot;
    const generation = hydrationGenerationRef.current + 1;
    hydrationGenerationRef.current = generation;
    setHydratedTargetSpot(null);
    setTargetHydrationFailed(false);

    if (!targetSpot || !targetSpot.id) {
      setIsHydratingTarget(false);
      return undefined;
    }

    setIsHydratingTarget(true);
    actions.spots
      .getHydratedById(targetSpot.id)
      .then(({ spot }) => {
        if (mountedRef.current && generation === hydrationGenerationRef.current) {
          setHydratedTargetSpot({ ...targetSpot, ...spot });
        }
      })
      .catch(() => {
        if (mountedRef.current && generation === hydrationGenerationRef.current) {
          setTargetHydrationFailed(true);
        }
      })
      .finally(() => {
        if (mountedRef.current && generation === hydrationGenerationRef.current) {
          setIsHydratingTarget(false);
        }
      });

    return () => {
      if (generation === hydrationGenerationRef.current) {
        hydrationGenerationRef.current += 1;
      }
    };
  }, [suggestion]);

  // A regenerated proposal for the same photo is a different answer, so the
  // point the owner set against the old one is dropped with it.
  useEffect(() => {
    setCorrectedPoint(null);
  }, [suggestion]);

  useEffect(() => {
    setImageFailed(false);
    setPhotoDimensions(null);
    setStageView('photo');
    setCorrectedPoint(null);
  }, [currentPhotoId]);

  const showStage = useCallback(
    view => {
      setStageView(view);

      if (view === 'map' && currentPhotoId) {
        setMapOpenedFor(currentPhotoId);
      }
    },
    [currentPhotoId]
  );

  // 1 shows the photo, 2 shows where it says it was taken.
  useEffect(() => {
    const onKeyDown = event => {
      if (
        superOpenRef.current ||
        event.defaultPrevented ||
        event.repeat ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        (event.target && typeof event.target.closest === 'function'
          ? Boolean(event.target.closest('input, textarea, select, [contenteditable]'))
          : false)
      ) {
        return;
      }

      if (event.key === '1' || event.key === '2') {
        event.preventDefault();
        showStage(event.key === '1' ? 'photo' : 'map');
        return;
      }

      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        skipRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showStage]);

  // The past Super Spots to ask about this session. They are optional: a failed
  // read only means nothing is asked.
  useEffect(() => {
    let cancelled = false;

    actions.autoModeration
      .getSuperSpotQuestions({ limit: SUPER_SPOT_QUESTIONS_PER_SESSION })
      .then(data => {
        if (!cancelled) {
          setSuperQuestions(Array.isArray(data && data.questions) ? data.questions : []);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  // All of them in a row, before the first decision: the proposals for the
  // photos ahead are being written meanwhile, so the wait is put to use. One
  // question every few photos broke the flow of deciding them. Questions that
  // arrive once the deciding has begun are not asked at all.
  useEffect(() => {
    if (
      superQuestion ||
      (!superRunRef.current && decidedCount > 0) ||
      superAsked >= SUPER_SPOT_QUESTIONS_PER_SESSION ||
      superQuestions.length === 0
    ) {
      return;
    }

    superRunRef.current = true;
    setSuperQuestion(superQuestions[0]);
    setSuperQuestions(current => current.slice(1));
    setSuperReason('');
  }, [decidedCount, superAsked, superQuestion, superQuestions]);

  useEffect(() => {
    superOpenRef.current = Boolean(superQuestion);
  }, [superQuestion]);

  // Twenty photos ahead, and never one taken beside the current photo: its answer
  // would be built without the spot this photo is about to create. Neighbours go
  // to the back of the queue instead, and nothing further is spent in advance.
  // While the strong model still has room they are asked for one at a time, so
  // each pick knows what the last one really spent; only once the fallback model
  // takes over are they asked for together.
  useEffect(() => {
    if (photos.length < 2 || isQueueLoading || suggestionStatus === 'loading') {
      return;
    }

    // Once the free allowance is gone every request is billed, and a photo
    // preloaded but never reached is billed for nothing. The current photo is
    // still asked for; only the reading ahead stops.
    if (!hasComplimentaryTokensLeft(tokenUsage)) {
      return;
    }

    const { preload: nextPhotos, deferred } = getPreloadPlan(photos);
    const reorderedQueue = getQueueWithDeferredLast(photos, deferred, subscriptionLevelsRef.current);

    if (reorderedQueue !== photos) {
      setPhotos(reorderedQueue);
      return;
    }

    nextPhotos.forEach(nextPhoto => {
      const url = getPhotoUrl(nextPhoto);

      if (url && !preloadedUrlsRef.current.has(url)) {
        const image = new Image();
        image.src = url;
        preloadedUrlsRef.current.add(url);
      }
    });

    const spotIndex = spotIndexRef.current;

    for (const nextPhoto of nextPhotos) {
      if (suggestionRequestsRef.current.has(nextPhoto.id)) {
        continue;
      }

      const candidateCount = spotIndex ? getCandidateSpotIds(spotIndex, nextPhoto).length : 0;
      const model = getAffordableModel(getCommittedUsage(), candidateCount);

      // Nothing free is left for this photo once what is in flight lands.
      if (!model) {
        break;
      }

      if (isFallbackModel(model)) {
        requestSuggestion(nextPhoto).catch(() => undefined);
        continue;
      }

      const strongModelBusy = [...pendingUsageRef.current].some(
        pending => !isFallbackModel(pending.model)
      );

      if (!strongModelBusy) {
        requestSuggestion(nextPhoto).catch(() => undefined);
      }

      break;
    }

    // A suggestion that was paid for is kept for as long as its photo is still
    // in the queue, wherever the reordering moved it. Only photos that have left
    // the session are dropped.
    const queuedIds = new Set(photos.map(photo => photo.id));
    suggestionRequestsRef.current.forEach((_request, photoId) => {
      if (!queuedIds.has(photoId)) {
        suggestionRequestsRef.current.delete(photoId);
      }
    });
  }, [
    photos,
    isQueueLoading,
    suggestionStatus,
    requestSuggestion,
    getCommittedUsage,
    tokenUsage,
    settledRequests,
  ]);

  // Clusters with nothing around them go last, and each one is ordered so its
  // most representative photo is judged first: that photo creates the spot and
  // becomes its main image, and the rest of the cluster then has something to be
  // assigned to. Ordering only - the model never decides the spot here.
  useEffect(() => {
    const groups = groupedPhotosRef.current;

    if (groups.length === 0 || isQueueLoading) {
      return;
    }

    groupedPhotosRef.current = [];

    Promise.all(
      getPhotoGroups(groups).map(async group => {
        if (group.length < 2) {
          return group;
        }

        try {
          const { photoId } = await actions.autoModeration.getGroupRepresentative({
            photoIds: group.map(photo => photo.id),
            model: pickModel(tokenUsageRef.current, 0),
          });

          return withRepresentativeFirst(group, photoId);
        } catch (error) {
          return group;
        }
      })
    ).then(ordered => {
      if (!mountedRef.current) {
        return;
      }

      const queued = ordered.flat();

      setGroupedCount(0);
      setPhotos(current => [...current, ...queued]);
      setSessionTotal(current => current + queued.length);
    });
  }, [isQueueLoading, groupedCount]);

  // The panel is scrolled down to read the candidate photos while deciding, and
  // the next photo has to start from the top whatever ended the last one.
  useEffect(() => {
    if (inspectorRef.current) {
      inspectorRef.current.scrollTop = 0;
    }
  }, [currentPhotoId]);

  useEffect(() => {
    if (feedbackOpen && feedbackRef.current) {
      feedbackRef.current.focus();
    }
  }, [feedbackOpen]);

  const finishCurrentPhoto = outcome => {
    const photoId = currentPhoto.id;

    setPhotos(current => current.filter(photo => photo.id !== photoId));
    setSessionStats(current => ({
      ...current,
      [outcome]: (current[outcome] || 0) + 1,
    }));
    setSuggestion(null);
    setSuggestionStatus('idle');
    setFeedbackOpen(false);
    setFeedback('');
    setNote('');
    setCreateOptions({ ...INITIAL_CREATE_OPTIONS });
    setActionError(null);
  };

  const registerCreatedSpot = createdSpot => {
    if (!createdSpot || !createdSpot.id) {
      return;
    }

    const isKnown = spotsRef.current.some(spot => spot.id === createdSpot.id);

    if (!isKnown) {
      setSpotUniverse([
        ...spotsRef.current,
        {
          id: createdSpot.id,
          slug: createdSpot.slug,
          lat: createdSpot.lat,
          lng: createdSpot.lng,
        },
      ]);
    }
  };

  // Everything that happens after a decision runs here: the queue never waits
  // for it, and whatever fails stays visible with a way to run it again.
  const runBackgroundTask = useCallback(task => {
    setBackgroundTasks(current => [
      ...current.filter(item => item.id !== task.id),
      { ...task, status: 'running', error: null },
    ]);

    Promise.resolve()
      .then(() => task.run())
      .then(() => {
        if (mountedRef.current) {
          setBackgroundTasks(current => current.filter(item => item.id !== task.id));
        }
      })
      .catch(error => {
        if (mountedRef.current) {
          setBackgroundTasks(current =>
            current.map(item => (item.id === task.id ? { ...item, status: 'failed', error } : item))
          );
        }
      });
  }, []);

  const startDescriptionTask = ({ spot, city }) => {
    if (!spot || !spot.id) {
      return;
    }

    runBackgroundTask({
      id: `description-${spot.id}`,
      label: `description · ${spot.name}`,
      run: () => generateSpotDescription({ spot, city, onUsage: countTokenUsage }),
    });
  };

  const closeSuperQuestion = () => {
    setSuperQuestion(null);
    setSuperReason('');
    setSuperAsked(current => current + 1);
  };

  const saveSuperQuestion = () => {
    const question = superQuestion;
    const reason = superReason.trim();

    if (!question || !reason) {
      return;
    }

    runBackgroundTask({
      id: `super-spot-${question.spotId}`,
      label: `super spot · ${question.name}`,
      run: () =>
        actions.autoModeration.saveSuperSpotReason({
          spotId: question.spotId,
          reason,
          source: 'past',
          photoId: question.photoId,
        }),
    });
    closeSuperQuestion();
  };

  // Leaves the photo exactly as it is: still pending, and its suggestion still
  // the current one, so reloading the queue brings it back without asking the
  // model again.
  const handleSkip = () => {
    if (!currentPhoto) {
      return;
    }

    finishCurrentPhoto('skipped');
  };

  skipRef.current = handleSkip;

  // A photo the model cannot even download will never produce a suggestion, and
  // the only two answers on offer - retry the same dead url, or skip - are not a
  // choice worth stopping for. The skipped count in the bar says how many went
  // by. An Unsplash photo whose file the backend confirmed gone was flagged
  // there and is never served again; anything else stays pending.
  useEffect(() => {
    if (suggestionStatus !== 'error' || !isMissingImageError(suggestionError)) {
      return;
    }

    skipRef.current();
  }, [suggestionStatus, suggestionError]);

  // Only the id and the type are read on the other side. A photo from either
  // Unsplash queue carries its whole Unsplash record, and there is no reason to
  // send it back.
  const getApplyPhoto = photo =>
    isUnsplashQueue(photo.queue) ? { id: photo.id, type: photo.type } : photo;

  // Every outcome that is not a spot ends the same way on a photo from the
  // unsplash collection: it leaves the backoffice queues and waits for somebody
  // to recognise it. That is true of needs_more_info, and the owner wants it
  // true of an abstention too - Auto Mod handing an Unsplash photo back for him
  // to look at by hand is work he never asked for.
  const parkUnsplashPhoto = photo =>
    actions.photos.editById(photo.id, { detectiveNeedsMoreData: true });

  const applySuggestion = async ({ suggestion: decision, photo, spotPoint }) => {
    if (decision.action === 'assign_spot') {
      return actions.spots.assign({ id: decision.targetSpot.id }, getApplyPhoto(photo), {
        suggestionId: decision.id,
      });
    }

    if (decision.action === 'create_spot') {
      let payload = createPayloadsRef.current.get(decision.id);

      if (!payload) {
        const proposedSlug = String(decision.newSpot.slug || '').trim().toLocaleLowerCase();

        if (slugsRef.current.has(proposedSlug)) {
          throw new Error(
            `The slug “${decision.newSpot.slug}” is already in use. Reject this suggestion so the model can be corrected.`
          );
        }

        // The owner's point wins over the proposed one. Everything else about
        // the spot - the name, the slug, the country - is what he approved.
        const nextPayload = buildCreateSpotPayload({
          photo,
          draft: spotPoint ? { ...decision.newSpot, ...spotPoint } : decision.newSpot,
        });
        payload = Object.freeze({
          ...nextPayload,
          photos: Object.freeze([...nextPayload.photos]),
          userPhotos: Object.freeze([...nextPayload.userPhotos]),
        });
        createPayloadsRef.current.set(decision.id, payload);
      }

      return actions.spots.create(payload, { suggestionId: decision.id, ownerPoint: spotPoint });
    }

    // A rejection has to go through the collection the photo actually lives in.
    // Sending an Unsplash photo to the user-photo endpoint would look like it
    // worked and change nothing.
    if (decision.action === 'reject') {
      if (isUnsplashQueue(photo.queue)) {
        return actions.photos.moderation.reject({
          id: photo.id,
          cause: Number(decision.rejection.cause),
        });
      }

      return actions.userPhotos.reject(
        { ...photo, cause: Number(decision.rejection.cause) },
        { suggestionId: decision.id }
      );
    }

    // The same flag the NEEDS INFO button sets in Mod. It is a backoffice flag
    // only: the photo stays published, and the app keeps showing it to users
    // asking whether they recognise the place, because that list is read from
    // the status and not from this field. The moment somebody answers with a
    // real point the app clears the flag and stamps detectiveLocatedAt, so the
    // photo comes back with a fresh proposal. Nothing here is one-way.
    if (decision.action === 'needs_more_info') {
      return parkUnsplashPhoto(photo);
    }

    throw new Error('This suggestion cannot be applied automatically.');
  };

  // The queue moves on as soon as the decision is taken; the requests run behind
  // it. Each step remembers whether it already succeeded, so retrying a failed
  // task never applies the same mutation twice.
  const startDecisionTask = ({ suggestion: decision, photo, options, feedbackText, spotPoint }) => {
    const abstained = isAbstention(decision.action);
    // An abstention on a user photo leaves it pending for Mod, which is the
    // point of it. On an Unsplash photo there is nobody waiting and nothing for
    // the owner to add, so it is parked exactly like needs_more_info.
    const parks = abstained && isUnsplashQueue(photo.queue);
    let acceptance = null;
    let parked = false;
    let reviewed = false;
    let promoted = false;
    let reasonSaved = false;

    runBackgroundTask({
      id: `decision-${decision.id}`,
      label: `${getActionPastTense(decision.action)} · @${
        (photo.user && photo.user.username) || 'unknown'
      }`,
      run: async () => {
        if (parks && !parked) {
          await parkUnsplashPhoto(photo);
          parked = true;
        }

        if (!abstained && !acceptance) {
          const result = await applySuggestion({ suggestion: decision, photo, spotPoint });
          acceptance = {
            applyResult: buildCompactApplyResult({ suggestion: decision, result }),
            createdSpot:
              decision.action === 'create_spot' && result && result.id
                ? { ...decision.newSpot, ...result }
                : null,
          };
        }

        if (!reviewed) {
          await actions.autoModeration.reviewSuggestion({
            suggestionId: decision.id,
            photoId: photo.id,
            outcome: 'accepted',
            ...(abstained && feedbackText ? { feedback: feedbackText } : {}),
            ...(acceptance ? { applyResult: acceptance.applyResult } : {}),
            // The backend refuses an approval whose spot does not sit where the
            // proposal said, so the point the owner set has to travel with it.
            ...(spotPoint ? { ownerPoint: spotPoint } : {}),
          });
          reviewed = true;
        }

        if (acceptance && acceptance.createdSpot) {
          // Super Spot is always a separate edit after the spot exists: that call
          // is what credits the uploader's extra points and notification, and it
          // only does so while the spot still holds their single photo.
          if (options.superSpot && !promoted) {
            await actions.spots.edit(acceptance.createdSpot, { isTop: true });
            promoted = true;
          }

          const superReasonText = String(options.superReason || '').trim();

          if (options.superSpot && superReasonText && !reasonSaved) {
            await actions.autoModeration.saveSuperSpotReason({
              spotId: acceptance.createdSpot.id,
              reason: superReasonText,
              source: 'new',
              photoId: photo.id,
            });
            reasonSaved = true;
          }

          registerCreatedSpot(acceptance.createdSpot);

          if (!options.skipDescription) {
            startDescriptionTask({
              spot: acceptance.createdSpot,
              city: (decision.newSpot && decision.newSpot.city) || '',
            });
          }
        }
      },
    });
  };

  const handleApprove = () => {
    if (!currentPhoto || !suggestion) {
      return;
    }

    const blockReason = getApprovalBlockReason(suggestion, currentPhoto);

    if (blockReason) {
      setActionError(new Error(blockReason));
      return;
    }

    startDecisionTask({
      suggestion,
      photo: currentPhoto,
      options: { ...createOptions },
      feedbackText: note.trim(),
      // Read here, while the photo is still the one on screen: the queue moves
      // on before the spot is written.
      spotPoint: correctedPoint,
    });
    finishCurrentPhoto(isAbstention(suggestion.action) ? 'deferred' : 'accepted');
  };

  const rejectSuggestionWith = text => {
    if (!currentPhoto || !suggestion) {
      return;
    }

    const trimmedFeedback = text.trim();

    if (!trimmedFeedback) {
      setActionError(new Error('Explain what the model got wrong before continuing.'));
      feedbackRef.current?.focus();
      return;
    }

    const decision = suggestion;
    const photo = currentPhoto;
    let reviewed = null;
    let rejected = false;

    runBackgroundTask({
      id: `decision-${decision.id}`,
      label: `feedback · @${(photo.user && photo.user.username) || 'unknown'}`,
      run: async () => {
        if (!reviewed) {
          reviewed = await actions.autoModeration.reviewSuggestion({
            suggestionId: decision.id,
            photoId: photo.id,
            outcome: 'rejected',
            feedback: trimmedFeedback,
          });
        }

        // On an Unsplash photo the backend reads the note, and when the note is
        // the owner telling it to turn the photo down it comes back as a reject
        // correction. The photo is rejected through the same call an approved
        // rejection uses, instead of staying pending to be judged again.
        const correction = reviewed && reviewed.correction;

        if (!rejected && isUnsplashQueue(photo.queue) && correction && correction.action === 'reject') {
          await actions.photos.moderation.reject({ id: photo.id, cause: Number(correction.cause) });
          rejected = true;
        }
      },
    });
    finishCurrentPhoto('challenged');
  };

  const handleRejectSuggestion = event => {
    event.preventDefault();
    rejectSuggestionWith(feedback);
  };

  // Read before the early returns: the session-complete screen has to show what
  // is still in flight, and that screen returns long before the topbar renders.
  const runningTasks = backgroundTasks.filter(task => task.status === 'running').length;
  const failedTasks = backgroundTasks.filter(task => task.status === 'failed');

  if (isQueueLoading && photos.length === 0) {
    return <QueueLoading />;
  }

  if (queueError && photos.length === 0) {
    return (
      <main className="AutoModeration">
        <div className="auto-page-state">
          <div>
            <span className="auto-kicker">Queue unavailable</span>
            <h1>Auto Mod could not load</h1>
            <p>{getErrorMessage(queueError)}</p>
            <button type="button" onClick={loadQueue}>Retry queue</button>
            <Link to="/moderation">Open Mod</Link>
          </div>
        </div>
      </main>
    );
  }

  if (!currentPhoto && (isLoadingDetective || isLoadingSafe || isLoadingUnsplash)) {
    return (
      <main className="AutoModeration">
        <div className="auto-page-state --loading">
          <div>
            <span className="auto-kicker">
              Auto Mod · {isLoadingDetective ? 'Detective' : isLoadingSafe ? 'Unsplash · safe' : 'Unsplash'}
            </span>
            <h1>Opening the lost photos</h1>
            <p>
              {isLoadingDetective
                ? 'The user photos are done and there are still complimentary tokens left. Gathering the Unsplash photos the app’s detectives have located.'
                : isLoadingSafe
                  ? 'Detective is done. Gathering Unsplash photos for the safe judge, which only shows you what it is sure of.'
                  : 'Gathering Unsplash photos nobody has located, that still carry the coordinate their camera wrote.'}
            </p>
            <div className="auto-loading-track"><span /></div>
          </div>
        </div>
      </main>
    );
  }

  if (!currentPhoto) {
    // Descriptions are written from this page, after the photo that created the
    // spot has already left the queue. The last one is usually still in flight
    // when the queue empties, so the session is not over until they land.
    const isFinishing = runningTasks > 0;
    const hasFailures = failedTasks.length > 0;
    const unsplashCursor = window.localStorage.getItem(UNSPLASH_CURSOR_KEY) || '';

    return (
      <main className="AutoModeration">
        <div className="auto-page-state">
          <div>
            <span className="auto-kicker">
              {isFinishing ? 'Keep this tab open' : hasFailures ? 'Unfinished work' : 'Session complete'}
            </span>
            <h1>
              {isFinishing
                ? `Finishing ${runningTasks} job${runningTasks === 1 ? '' : 's'}…`
                : hasFailures
                ? `${failedTasks.length} job${failedTasks.length === 1 ? '' : 's'} never finished`
                : 'No photos left in this Auto Mod session'}
            </h1>
            <p>
              {sessionStats.accepted} proposal{sessionStats.accepted === 1 ? '' : 's'} applied and{' '}
              {sessionStats.deferred} left for Mod. {sessionStats.challenged} returned with feedback
              and {sessionStats.skipped} skipped for later.
            </p>
            {groupedCount > 0 && (
              <p>
                {groupedCount} grouped photo{groupedCount === 1 ? '' : 's'} never entered this
                session. Grouped photos are decided together in Mod.
              </p>
            )}

            {/* A queue that never opened and a queue that opened empty look
                identical from here, and the difference is the whole answer to
                "why am I not being shown anything". Each one says which it was.
                A count of null means the phase was never reached: Detective only
                opens once the user photos are gone, Unsplash only once Detective
                is, and both only while the day's free tokens still have room. */}
            <ul className="auto-page-phases">
              {[
                { label: 'Detective', count: detectiveCount, blockedBy: 'the user photos' },
                { label: 'Unsplash', count: unsplashCount, blockedBy: 'Detective' },
              ].map(phase => (
                <li key={phase.label}>
                  <strong>{phase.label}</strong>
                  <span>
                    {phase.count === null
                      ? hasComplimentaryTokensLeft(tokenUsage)
                        ? `never opened — it waits for ${phase.blockedBy} to run out first`
                        : 'never opened — today’s complimentary tokens are gone'
                      : phase.count === 0
                        ? 'opened and had nothing waiting'
                        : `opened with ${phase.count} photo${phase.count === 1 ? '' : 's'}`}
                  </span>
                </li>
              ))}
            </ul>

            {unsplashCount === 0 && (
              <p>
                The Unsplash pile is read one block at a time and this session’s block held nothing
                new. The next session carries on from{' '}
                <code>{unsplashCursor || 'the top of the pile'}</code>.
              </p>
            )}

            {backgroundTasks.length > 0 && (
              <ul className="auto-page-tasks">
                {backgroundTasks.map(task => (
                  <li className={task.status === 'failed' ? '--failed' : ''} key={task.id}>
                    <strong>{task.label}</strong>
                    <span>
                      {task.status === 'failed' ? getErrorMessage(task.error) : 'Running…'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {failedTasks.length > 0 && (
              <button
                className="--failed"
                type="button"
                onClick={() => failedTasks.forEach(runBackgroundTask)}
              >
                Retry {failedTasks.length} failed
              </button>
            )}
            <button type="button" onClick={loadQueue}>Reload pending queue</button>
            <Link to="/moderation">Open Mod</Link>
          </div>
        </div>
      </main>
    );
  }

  // Screened photos count as handled here, so the photo index keeps step with
  // what is left, but never as decided: nobody looked at them.
  const handledCount =
    sessionStats.accepted +
    sessionStats.challenged +
    sessionStats.deferred +
    sessionStats.skipped +
    (sessionStats.screened || 0);
  const reportedQueueCount =
    queueMetadata && queueMetadata.count != null ? Number(queueMetadata.count) : null;
  // The bar keeps the strong model lit while it can still take a cheap photo,
  // which is exactly how long it is still in use.
  const activeModel = pickModel(tokenUsage, 0);
  const queueBadge = getQueueBadge(currentQueue, {
    nearSpots: Boolean(queueMetadata && queueMetadata.nearSpots),
  });

  return (
    <main className="AutoModeration">
      <header className="auto-topbar">
        <div className={`auto-training-status --${currentQueue}`}>
          <span className="auto-live-dot" />
          <strong>{queueBadge.label}</strong>
          <span>{queueBadge.description}</span>
        </div>

        <div className="auto-session-summary" aria-label="Auto Mod session summary">
          <button
            className={`auto-topbar-task auto-bench-toggle ${benchRunning ? '--running' : ''}`}
            type="button"
            disabled={!benchRunning && !spotIndexRef.current}
            onClick={benchRunning ? stopBench : startBench}
            title={
              benchRunning
                ? 'Stop the bench'
                : 'Replay your past /moderation decisions through the Unsplash judge, on free TERRA tokens only'
            }
          >
            {benchRunning ? 'Bench running · stop' : 'Bench'}
            {benchStats.done > 0 && ` · ${benchStats.done} done`}
            {benchStats.remaining != null && ` · ${benchStats.remaining} left`}
            {!benchRunning && benchStats.stoppedFor && ` · ${benchStats.stoppedFor}`}
          </button>
          {runningTasks > 0 && (
            <span className="auto-topbar-task" title="Applied in the background">
              {runningTasks} pending…
            </span>
          )}
          {failedTasks.length > 0 && (
            <button
              className="auto-topbar-task --failed"
              type="button"
              title={failedTasks
                .map(task => `${task.label}: ${getErrorMessage(task.error)}`)
                .join('\n')}
              onClick={() => failedTasks.forEach(runBackgroundTask)}
            >
              {failedTasks.length} failed · retry
            </button>
          )}
          <span><strong>{photos.length}</strong> left</span>
          <span title="Photos further down the queue whose proposal is already in hand">
            <strong>{readyAheadCount}</strong> ready
          </span>
          <span><strong>{sessionStats.accepted}</strong> applied</span>
          <span><strong>{sessionStats.deferred}</strong> to Mod</span>
          <span><strong>{sessionStats.challenged}</strong> challenged</span>
          {sessionStats.skipped > 0 && <span><strong>{sessionStats.skipped}</strong> skipped</span>}
          {sessionStats.screened > 0 && (
            <span title="Photos the safe judge was not sure of. They wait untouched for the regular Unsplash queue.">
              <strong>{sessionStats.screened}</strong> not sure
            </span>
          )}
          {groupedCount > 0 && (
            <span title="Grouped photos are left for /moderation">
              <strong>{groupedCount}</strong> grouped
            </span>
          )}
          {Number.isFinite(reportedQueueCount) && <span>{reportedQueueCount} pending overall</span>}
        </div>
      </header>

      <TokenBudgetBar usage={tokenUsage} activeModel={activeModel} />

      {superQuestion && (
        <div className="auto-super-question" role="dialog" aria-label="Why is this a Super Spot?">
          <div className="auto-super-question-card">
            <img src={superQuestion.imageUrl} alt={superQuestion.name} />
            <div className="auto-super-question-body">
              <span className="auto-kicker">
                Super Spot · {superAsked + 1} of{' '}
                {Math.min(SUPER_SPOT_QUESTIONS_PER_SESSION, superAsked + 1 + superQuestions.length)}
              </span>
              <h2>
                {superQuestion.name}
                {superQuestion.from ? ` · ${superQuestion.from}` : ''}
              </h2>
              {superQuestion.country && <p>{superQuestion.country}</p>}
              <label>
                Why is this a Super Spot?
                <textarea
                  autoFocus
                  rows={4}
                  maxLength={MAX_FEEDBACK_LENGTH}
                  value={superReason}
                  onChange={event => setSuperReason(event.target.value)}
                />
              </label>
              <div className="auto-super-question-actions">
                <button
                  className="auto-approve-action"
                  type="button"
                  disabled={!superReason.trim()}
                  onClick={saveSuperQuestion}
                >
                  Save
                </button>
                <button className="auto-skip-action" type="button" onClick={closeSuperQuestion}>
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`auto-workspace --${
          suggestionStatus === 'ready' ? getSuggestionTone(suggestion) : 'idle'
        }`}
        style={suggestionStatus === 'ready' ? getSuggestionToneStyle(suggestion) : undefined}
      >
        <section className="auto-photo-stage" aria-label="Photo under model review">
          <div className="auto-photo-index">
            <span>Photo</span>
            <strong>{String(handledCount + 1).padStart(2, '0')}</strong>
            <small>of {String(sessionTotal).padStart(2, '0')}</small>
            <span className={`auto-photo-queue --${currentQueue}`} title={queueBadge.description}>
              {queueBadge.label}
            </span>
          </div>

          <div className="auto-stage-tabs" role="tablist" aria-label="Photo or map">
            <button
              type="button"
              role="tab"
              aria-selected={stageView === 'photo'}
              className={stageView === 'photo' ? '--active' : ''}
              onClick={() => showStage('photo')}
            >
              Photo <kbd>1</kbd>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={stageView === 'map'}
              className={stageView === 'map' ? '--active' : ''}
              onClick={() => showStage('map')}
            >
              Map <kbd>2</kbd>
            </button>
          </div>

          <div className="auto-photo-frame" key={currentPhoto.id}>
            <div
              className={`auto-stage-layer --photo ${stageView === 'map' ? '--hidden' : ''}`}
            >
              {photoUrl && !imageFailed ? (
                <img
                  src={photoUrl}
                  alt="Pending NoFilter submission"
                  onLoad={event => {
                    const { naturalHeight, naturalWidth } = event.currentTarget;

                    if (naturalHeight && naturalWidth) {
                      setPhotoDimensions({
                        photoId: currentPhoto.id,
                        height: naturalHeight,
                        width: naturalWidth,
                      });
                    }
                  }}
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <div className="auto-photo-unavailable">
                  <strong>Photo preview unavailable</strong>
                  <span>Open this item in Mod before making a decision.</span>
                </div>
              )}
            </div>

            {/* Mounted on the first look and kept alive until the next photo, so
                street view, zoom and panning survive switching back and forth. */}
            {hasOpenedMap && (
              <div
                className={`auto-stage-layer --map ${stageView === 'map' ? '' : '--hidden'}`}
              >
                <LocationMap
                  position={photoPosition}
                  trust={gpsTrust}
                  spots={mapSpots}
                  targetSpotId={
                    (suggestion && suggestion.targetSpot && suggestion.targetSpot.id) || ''
                  }
                  proposedSpot={
                    (suggestion && suggestion.action === 'create_spot' && suggestion.newSpot) || null
                  }
                  correctedPoint={correctedPoint}
                  onPickPoint={
                    suggestion && suggestion.action === 'create_spot' ? setCorrectedPoint : null
                  }
                />
              </div>
            )}
          </div>

          <footer className="auto-photo-footer">
            <strong>@{currentPhoto.user?.username || 'unknown'}</strong>
            {isDetective && currentPhoto.unsplashLocation?.name && (
              <span title="What Unsplash says, which is why this photo is in Detective">
                Unsplash: {currentPhoto.unsplashLocation.name}
              </span>
            )}
          </footer>
        </section>

        <aside ref={inspectorRef} className="auto-inspector" aria-label="Model suggestion">
          {suggestionStatus === 'loading' && <SuggestionLoading />}

          {suggestionStatus === 'error' && (
            <div className="auto-suggestion-error">
              <span className="auto-kicker">
                {terminalSuggestionError ? 'Queue item changed' : 'Suggestion unavailable'}
              </span>
              <h1>
                {terminalSuggestionError
                  ? 'This photo is no longer available here'
                  : 'The model could not finish this photo'}
              </h1>
              <p>{getErrorMessage(suggestionError)}</p>
              {terminalSuggestionError ? (
                <>
                  <p>The photo changed elsewhere. Skip this stale copy or refresh the pending queue.</p>
                  <button type="button" onClick={() => finishCurrentPhoto('skipped')}>
                    Skip stale photo & continue
                  </button>
                  <button
                    className="auto-secondary-action"
                    type="button"
                    disabled={isQueueLoading}
                    onClick={loadQueue}
                  >
                    {isQueueLoading ? 'Refreshing queue…' : 'Reload pending queue'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setSuggestionRetry(value => value + 1)}>
                    Retry suggestion
                  </button>
                  {/* Some Unsplash photos are simply gone from their CDN, and no
                      number of retries brings them back. Without this the queue
                      stopped dead on one of them. Skipping leaves the photo
                      exactly as it is, pending and with no decision recorded, so
                      it comes back the next time the queue is read. */}
                  <button
                    className="auto-secondary-action"
                    type="button"
                    onClick={handleSkip}
                    title="Leave it pending and move to the next photo"
                  >
                    Skip &amp; continue <kbd>S</kbd>
                  </button>
                </>
              )}
              <Link to="/moderation">Review in Mod</Link>
            </div>
          )}

          {suggestionStatus === 'ready' && suggestion && (
            <>
              <button
                className={`auto-gps-trust --${gpsTrust.level}`}
                type="button"
                onClick={() => showStage(stageView === 'map' ? 'photo' : 'map')}
                title="Show this photo on the map"
              >
                <span className="auto-gps-dot" style={{ background: gpsTrust.color }} />
                <span className="auto-gps-body">
                  <strong>{gpsTrust.label}</strong>
                  <span>{gpsTrust.summary}</span>
                  {gpsTrust.correctionDistanceMeters !== null && (
                    <span>
                      Moved {Math.round(gpsTrust.correctionDistanceMeters)} m from the camera point
                    </span>
                  )}
                </span>
                <span className="auto-gps-open">
                  {stageView === 'map' ? 'Photo' : 'Map'} <kbd>{stageView === 'map' ? 1 : 2}</kbd>
                </span>
              </button>

              <SuggestionPanel
                suggestion={suggestion}
                photo={currentPhoto}
                hydratedTargetSpot={hydratedTargetSpot}
                isHydratingTarget={isHydratingTarget}
                targetHydrationFailed={targetHydrationFailed}
              />

              <div className="auto-decision-dock">
                {actionError && (
                  <div className="auto-action-error" role="alert">
                    <span>{getErrorMessage(actionError)}</span>
                    <button type="button" onClick={() => setActionError(null)}>Dismiss</button>
                  </div>
                )}

                {feedbackOpen ? (
                  <form className="auto-feedback-form" onSubmit={handleRejectSuggestion}>
                    <div>
                      <span className="auto-kicker">Teach the model</span>
                      <label className="auto-feedback-label" htmlFor="auto-moderation-feedback">
                        What did it get wrong?
                      </label>
                    </div>
                    <div className="auto-feedback-quick">
                      {[
                        ...(suggestion.action === 'assign_spot' ? [QUICK_ASSIGN_FEEDBACK] : []),
                        ...QUICK_FEEDBACK,
                      ].map(text => (
                        <button type="button" key={text} onClick={() => rejectSuggestionWith(text)}>
                          {text}
                        </button>
                      ))}
                    </div>
                    <textarea
                      id="auto-moderation-feedback"
                      ref={feedbackRef}
                      value={feedback}
                      onChange={event => setFeedback(event.target.value)}
                      placeholder="Be specific: wrong spot, wrong name, unsafe rejection, missing landmark…"
                      aria-describedby="auto-feedback-help auto-feedback-count"
                      maxLength={MAX_FEEDBACK_LENGTH}
                      rows={4}
                    />
                    <div className="auto-feedback-meta">
                      <p id="auto-feedback-help">
                        This photo stays pending for Mod. It only leaves this Auto Mod session.
                      </p>
                      <span id="auto-feedback-count">
                        {feedback.length.toLocaleString()} / {MAX_FEEDBACK_LENGTH.toLocaleString()}
                      </span>
                    </div>
                    <div className="auto-feedback-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setFeedbackOpen(false);
                          setActionError(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" disabled={!feedback.trim()}>
                        Send feedback &amp; continue
                      </button>
                    </div>
                  </form>
                ) : isAbstention(suggestion.action) ? (
                  <div className="auto-unknown-actions">
                    <textarea
                      value={note}
                      onChange={event => setNote(event.target.value)}
                      placeholder="Optional: what should it have decided, and why? Recorded for the next prompt review."
                      maxLength={MAX_FEEDBACK_LENGTH}
                      rows={3}
                    />
                    <div className="auto-unknown-buttons">
                      <button
                        className="auto-approve-action"
                        type="button"
                        onClick={handleApprove}
                      >
                        Next photo
                      </button>
                      <button
                        className="auto-skip-action"
                        type="button"
                        onClick={handleSkip}
                        title="Leave it pending and come back to it later"
                      >
                        Skip <kbd>S</kbd>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="auto-decision-actions">
                    {suggestion.action === 'create_spot' && (
                      <div className="auto-create-options">
                        <label>
                          <input
                            type="checkbox"
                            checked={createOptions.skipDescription}
                                  onChange={event =>
                              setCreateOptions(current => ({
                                ...current,
                                skipDescription: event.target.checked,
                              }))
                            }
                          />
                          No description
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={createOptions.superSpot}
                                  onChange={event =>
                              setCreateOptions(current => ({
                                ...current,
                                superSpot: event.target.checked,
                              }))
                            }
                          />
                          Super Spot
                        </label>
                        {createOptions.superSpot && (
                          <input
                            className="auto-super-reason"
                            type="text"
                            maxLength={MAX_FEEDBACK_LENGTH}
                            value={createOptions.superReason}
                            placeholder="Why is it a Super Spot? (optional)"
                            onChange={event =>
                              setCreateOptions(current => ({
                                ...current,
                                superReason: event.target.value,
                              }))
                            }
                          />
                        )}
                      </div>
                    )}
                    <button
                      className="auto-approve-action"
                      type="button"
                      disabled={Boolean(approvalBlockReason || suggestionValidationError)}
                      onClick={handleApprove}
                    >
                      Approve &amp; apply
                    </button>
                    <button
                      className="auto-challenge-action"
                      type="button"
                      onClick={() => {
                        setFeedbackOpen(true);
                        setActionError(null);
                      }}
                    >
                      Reject suggestion
                    </button>
                    <button
                      className="auto-skip-action"
                      type="button"
                      onClick={handleSkip}
                      title="Leave it pending and come back to it later"
                    >
                      Skip <kbd>S</kbd>
                    </button>
                    {approvalBlockReason && <p>{approvalBlockReason}</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </aside>
      </div>
    </main>
  );
};

export default AutoModeration;
